import type { IQuickInstrumentListItem } from '@brushfire/types'

/** UI status labels for Direct Instrument Building cards (BFP-999). */
export type DirectInstrumentUiStatus =
  | 'Draft'
  | 'Live'
  | 'Paused'
  | 'Stopped'
  | 'Completed'

export type DirectInstrumentStatusFilter = 'all' | DirectInstrumentUiStatus

export const DIRECT_INSTRUMENT_STATUS_FILTERS: ReadonlyArray<{
  value: DirectInstrumentStatusFilter
  label: string
}> = [
  { value: 'all', label: 'All' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Live', label: 'Live' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Stopped', label: 'Stopped' },
  { value: 'Completed', label: 'Completed' },
]

/** Map API instrument status → Direct tab display label. */
export function mapApiStatusToUiStatus(status: string): DirectInstrumentUiStatus {
  const normalized = status.trim().toLowerCase()
  switch (normalized) {
    case 'draft':
      return 'Draft'
    case 'active':
      return 'Live'
    case 'paused':
      return 'Paused'
    case 'stopped':
      return 'Stopped'
    case 'completed':
    case 'expired':
    case 'closed':
      return 'Completed'
    default:
      return 'Draft'
  }
}

export type DirectInstrumentPauseReasonLabel = 'Reached Target' | 'Manually Paused'

/**
 * BFP-1129 — kept local so Sensemaker compiles when CI checks out a
 * `@brushfire/types` build that does not yet export these fields.
 */
export type QuickInstrumentPauseReason = 'manual' | 'target_reached'

export type QuickInstrumentListItem = IQuickInstrumentListItem & {
  pauseReason?: QuickInstrumentPauseReason | null
  responseTarget?: number | null
}

/** BFP-1129 — why a paused card is paused; null when not paused. Legacy rows = manual. */
export function pauseReasonLabel(
  item: Pick<QuickInstrumentListItem, 'status' | 'pauseReason'>,
): DirectInstrumentPauseReasonLabel | null {
  if (mapApiStatusToUiStatus(item.status) !== 'Paused') return null
  return item.pauseReason === 'target_reached' ? 'Reached Target' : 'Manually Paused'
}

/** BFP-1129 — Sign-up Survey auto-paused at target → Resume opens the Resume Survey dialog. */
export function isSignupSurveyPausedAtTarget(
  item: Pick<QuickInstrumentListItem, 'status' | 'pauseReason' | 'isSignupSurvey'>,
): boolean {
  return item.isSignupSurvey === true && pauseReasonLabel(item) === 'Reached Target'
}

/** Map UI filter pill → API `status` query param (omit for All). */
export function mapUiFilterToApiStatus(
  filter: DirectInstrumentStatusFilter,
): string | undefined {
  switch (filter) {
    case 'Draft':
      return 'draft'
    case 'Live':
      return 'active'
    case 'Paused':
      return 'paused'
    case 'Stopped':
      return 'stopped'
    case 'Completed':
      return 'completed'
    default:
      return undefined
  }
}

export function formatUsdAmount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Total rewards distributed to Signal Providers: completed responses × per-response reward. */
export function budgetUsedUsdFromDistributedRewards(
  rewardPerResponse: number | null | undefined,
  responseCount: number | null | undefined,
): number {
  if (rewardPerResponse == null || !Number.isFinite(rewardPerResponse) || rewardPerResponse <= 0) {
    return 0
  }
  const count =
    responseCount == null || !Number.isFinite(responseCount) ? 0 : Math.max(0, responseCount)
  return Math.round(rewardPerResponse * count * 100) / 100
}

export function resolveBudgetUsedUsd(item: {
  budgetUsedUsd?: number | null
  rewardPerResponse?: number | null
  responseCount?: number | null
}): number {
  if (typeof item.budgetUsedUsd === 'number' && Number.isFinite(item.budgetUsedUsd)) {
    return item.budgetUsedUsd
  }
  return budgetUsedUsdFromDistributedRewards(item.rewardPerResponse, item.responseCount)
}

/** Direct Instrument card — Figma `M/D/YY` (UTC, so the label does not shift by timezone). */
export function formatCreatedDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const year = String(d.getUTCFullYear()).slice(-2)
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${year}`
}

/** Dashboard Direct Instruments table — same outlined-pill tokens as Recent Campaigns. */
export const DIRECT_INSTRUMENT_STATUS_PILL: Record<
  DirectInstrumentUiStatus,
  { label: string; bg: string; border: string; color: string }
> = {
  Draft: {
    label: 'Draft',
    bg: '#fef3c7',
    border: '#f59e0b',
    color: '#92400e',
  },
  Live: {
    label: 'Live',
    bg: '#d1fae5',
    border: '#10b981',
    color: '#065f46',
  },
  Paused: {
    label: 'Paused',
    bg: '#fff7ed',
    border: '#fb923c',
    color: '#9a3412',
  },
  Stopped: {
    label: 'Stopped',
    bg: '#f1f5f9',
    border: '#94a3b8',
    color: '#475569',
  },
  Completed: {
    label: 'Completed',
    bg: '#e3f2fd',
    border: '#3285d7',
    color: '#3b82f6',
  },
}

export function matchesDirectInstrumentSearch(name: string, query: string): boolean {
  const trimmed = query.trim()
  if (!trimmed) return true
  return name.toLowerCase().includes(trimmed.toLowerCase())
}

export function matchesDirectInstrumentFilter(
  uiStatus: DirectInstrumentUiStatus,
  filter: DirectInstrumentStatusFilter,
): boolean {
  if (filter === 'all') return true
  return uiStatus === filter
}

export function sortDirectInstrumentsByName<T extends { name: string }>(
  items: T[],
  direction: 'asc' | 'desc',
): T[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    return cmp * factor
  })
}

export function sortDirectInstrumentsByCreatedDesc<T extends { createdAt: string }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function displayCreatedBy(
  item: IQuickInstrumentListItem,
  viewer?: { id?: string | null; name?: string | null } | null,
): string {
  const stored = item.createdByName?.trim()
  if (stored && !stored.includes('@')) return stored

  // Cards created before name was persisted — show the same profile name as AppShell.
  const viewerName = viewer?.name?.trim()
  const isOwnInstrument =
    Boolean(viewer?.id) &&
    Boolean(item.createdByUserId) &&
    viewer!.id === item.createdByUserId
  if (isOwnInstrument && viewerName && !viewerName.includes('@')) {
    return viewerName
  }

  return '—'
}
